import React from "react";
import { Col, Container, Image, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { isMobile } from "react-device-detect";
import AppSocialLinks from "../../components/AppSocialLinks";
import AppStoreBadges from "../../components/AppStoreBadges";
import YouTubeShort from "../../components/YouTubeShort";
import { HOW_TO_PATH, howToVideos, promoVideo } from "./reassistVideos";

// Mirrors the App Store and Google Play descriptions. Features only one
// platform has say which, so Android users aren't promised Siri.
const features = [
  {
    title: "For You",
    description:
      "On iPhone, the first tab opens on what matters now: a featured deal, your next showing with directions, the countdown to your next closing, today's route, and a client's home anniversary with a note ready to send.",
  },
  {
    title: "Every Listing at a Glance",
    description:
      "Photo-forward cards with status, price, days on market, and checklist progress. Filter by status or city, sort your way, search by address or MLS #, or import from an MLS CSV export.",
  },
  {
    title: "Your Day, Planned",
    description:
      "Book buyer showings, showings on your listings, and open house times, with a reminder an hour before each. Today's agenda turns them into a driving route in appointment order and logs the day's miles for your client in one tap. When the buyer goes under contract, the showing becomes the deal. Every listing sits on a map with its price.",
  },
  {
    title: "Never Miss a Contract Deadline",
    description:
      "Earnest money, inspection, appraisal, financing, and walkthrough in the order they come due, filled in from the contract and closing dates in one tap. When a closing date passes, one tap marks it sold, moves the date, or records that it fell through.",
  },
  {
    title: "Checklists for Both Sides",
    description:
      "A checklist that follows how deals actually move, from Pre-Listing to Closing & After. Represent the buyer? Flip one switch and the workflow adapts.",
  },
  {
    title: "Know What You Keep",
    description:
      "Your net profit for the year, after splits, spending, and miles, free. Commission after your brokerage split, fees, and annual cap, take-home so far, a yearly goal, and where your gross went.",
  },
  {
    title: "Reports That Plan Ahead",
    description:
      "With Pro, see what your pending deals will pay and where the year ends, what to set aside before each estimated-tax due date, and a Profit & Loss by month against last year. Send any seller a report of their showings, feedback, and what's next, free.",
  },
  {
    title: "Expenses, Miles & Tax Time",
    description:
      "Log expenses and mileage against the deal they belong to. With Pro, hand your accountant a year-end packet mapped to Schedule C, plus profit per deal.",
  },
  {
    title: "Your Card & Your People",
    description:
      "A digital business card in five designs with your photo, logo, office details, and a QR code, texted to a client in one tap. Buyers, sellers, lenders, and inspectors linked to their listings, with dated notes on every deal.",
  },
  {
    title: "Hands-Free",
    description:
      "Ask Siri what's due today or log miles in English or Spanish, see today's stops and log from your Apple Watch, find listings from Spotlight, and keep today and your next closing on widgets. On Android, widgets and app shortcuts do the same.",
  },
  {
    title: "Private by Design",
    description:
      "No account, no ads, no tracking. Unlimited listings and contacts, free. Your data stays on your device, backed up to a file you keep. On iPhone, pick from four colour themes.",
  },
];

const RealEstateAssist = () => {
  const appIcon = require("../../assets/apps/reassist/reassist_icon.png");
  // The three-phone fan from the App Store header artwork.
  const heroPhones = require("../../assets/apps/reassist/hero-phones.webp");
  const screenshots = [
    {
      id: 0,
      image: require("../../assets/apps/reassist/reassist_foryou.jpg"),
      alt: "For You with a featured deal, today's next showing, and the year at a glance",
    },
    {
      id: 1,
      image: require("../../assets/apps/reassist/reassist_listings.jpg"),
      alt: "Listings grouped by city with status, price, and days on market",
    },
    {
      id: 2,
      image: require("../../assets/apps/reassist/reassist_detail.jpg"),
      alt: "Listing detail with key dates and contract deadlines",
    },
    {
      id: 3,
      image: require("../../assets/apps/reassist/reassist_checklist.jpg"),
      alt: "Projected take-home, the seller report, and the listing checklist",
    },
    {
      id: 4,
      image: require("../../assets/apps/reassist/reassist_agenda.jpg"),
      alt: "Today's agenda of buyer showings and showings on your listings",
    },
    {
      id: 5,
      image: require("../../assets/apps/reassist/reassist_map.jpg"),
      alt: "Listing map with price pins colored by status and a listing card",
    },
    {
      id: 6,
      image: require("../../assets/apps/reassist/reassist_financials.jpg"),
      alt: "Financials with take-home, yearly goal, and the year's earnings",
    },
    {
      id: 7,
      image: require("../../assets/apps/reassist/reassist_pipeline.jpg"),
      alt: "Pipeline: what pending deals will pay and where the year ends",
    },
    {
      id: 8,
      image: require("../../assets/apps/reassist/reassist_taxes.jpg"),
      alt: "Quarterly estimated taxes with what to set aside for each due date",
    },
    {
      id: 9,
      image: require("../../assets/apps/reassist/reassist_seller_report.jpg"),
      alt: "A seller report of the listing's showings and feedback",
    },
  ];

  return (
    <>
      <Container fluid style={styles.hero}>
        <div style={styles.heroArc} className="d-none d-lg-block" aria-hidden="true" />
        <Container style={styles.heroInner}>
          <Row className="align-items-center">
            <Col xs={12} lg={6} className="text-center text-lg-start">
              <p style={styles.headline}>
                <span style={styles.headlineLine}>Every deal.</span>
                <span style={styles.headlineLine}>Every deadline.</span>
                <span style={{ ...styles.headlineLine, ...styles.headlineAccent }}>
                  One app.
                </span>
              </p>
              <p style={styles.heroSubline} className="mx-auto mx-lg-0">
                Listings, contract deadlines and commissions, built for real
                estate agents. On iPhone and Android, with your data on your
                device or in your own cloud storage.
              </p>
              <div
                style={styles.brandRow}
                className="justify-content-center justify-content-lg-start"
              >
                <Image src={appIcon} style={styles.brandIcon} alt="" />
                <h1 style={styles.brandName}>Real Estate Agent Assist</h1>
              </div>
              <div className="mt-4">
                <AppStoreBadges
                  height={isMobile ? 48 : 54}
                  className="justify-content-center justify-content-lg-start"
                />
              </div>
              <div className="mt-4">
                <AppSocialLinks
                  label="Follow @reassistapp"
                  tone="dark"
                  className="justify-content-center justify-content-lg-start"
                />
              </div>
            </Col>
            <Col xs={12} lg={6} className="mt-5 mt-lg-0">
              <Image
                fluid
                src={heroPhones}
                style={styles.heroPhones}
                alt="Real Estate Agent Assist on iPhone: a listing's key dates, the listings list, and financials"
              />
            </Col>
          </Row>
        </Container>
      </Container>
      <Container fluid style={styles.promoContainer}>
        <Container>
          <Row className="mb-4">
            <h2 style={styles.sectionTitle}>See it in action</h2>
          </Row>
          <Row className="justify-content-center">
            <Col xs={10} sm={6} md={4} lg={3}>
              <YouTubeShort
                id={promoVideo.id}
                title={promoVideo.title}
                maxWidth={300}
              />
            </Col>
          </Row>
        </Container>
      </Container>
      <Container fluid style={styles.screenshotsContainer}>
        <Container>
          <Row className="mb-4">
            <h2 style={styles.sectionTitle}>A look inside</h2>
          </Row>
          <Row className="justify-content-center">
            {screenshots.map((shot) => (
              <Col xs={6} md={3} key={shot.id} style={styles.screenshotCol}>
                <Image
                  fluid
                  src={shot.image}
                  style={styles.screenshot}
                  alt={shot.alt}
                />
              </Col>
            ))}
          </Row>
        </Container>
      </Container>
      <Container fluid style={styles.featuresContainer}>
        <Container>
          <Row className="mb-4">
            <h2 style={styles.sectionTitle}>Features</h2>
          </Row>
          <Row>
            {features.map((feature) => (
              <Col xs={12} md={6} lg={4} key={feature.title} className="mb-4">
                <div style={styles.featureCard}>
                  <h5 style={styles.featureTitle}>{feature.title}</h5>
                  <p style={styles.featureDescription}>{feature.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </Container>
      <Container fluid style={styles.howItWorksContainer}>
        <Container>
          <Row className="mb-4">
            <h2 style={styles.sectionTitle}>See how it works</h2>
          </Row>
          <Row className="justify-content-center">
            {howToVideos.slice(0, 3).map((video) => (
              <Col xs={4} md={3} key={video.slug} style={styles.howToCol}>
                <Link
                  to={`${HOW_TO_PATH}#${video.slug}`}
                  className="footer-link"
                  aria-label={`Watch: ${video.title}`}
                >
                  <div style={styles.howToThumbFrame}>
                    <img
                      src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
                      alt=""
                      loading="lazy"
                      style={styles.howToThumb}
                    />
                  </div>
                  <p style={styles.howToTitle}>{video.title}</p>
                </Link>
              </Col>
            ))}
          </Row>
          <Row className="justify-content-center mt-2">
            <Col xs="auto">
              <Link to={HOW_TO_PATH} className="btn btn-outline-light">
                All {howToVideos.length} how-to videos
              </Link>
            </Col>
          </Row>
        </Container>
      </Container>
      <Container fluid style={styles.infoContainer}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col xs={12} md={8}>
              <p style={styles.infoText}>
                Real Estate Agent Assist is made by Cross Vision Development Studios
                and published by ACJ Operations LLC.
              </p>
              <p style={styles.infoText}>
                <Link to="/apps/real-estate-assist/privacy" className="footer-link">
                  Privacy Policy
                </Link>
                {" · "}
                <Link to="/apps/real-estate-assist/support" className="footer-link">
                  Support
                </Link>
                {" · "}
                <Link to={HOW_TO_PATH} className="footer-link">
                  How-to videos
                </Link>
                {" · "}
                <Link to="/contact" className="footer-link">
                  Contact
                </Link>
              </p>
            </Col>
          </Row>
        </Container>
      </Container>
    </>
  );
};

export default RealEstateAssist;

const styles = {
  brandIcon: {
    borderRadius: 13,
    boxShadow: "0 4px 12px rgba(61, 46, 36, 0.25)",
    height: 56,
    width: 56,
  },
  brandName: {
    color: "#3D2E24",
    fontSize: isMobile ? 22 : 26,
    fontWeight: 600,
    margin: 0,
  },
  brandRow: {
    alignItems: "center",
    display: "flex",
    gap: 16,
  },
  headline: {
    color: "#3D2E24",
    fontFamily: 'ui-serif, "New York", "Iowan Old Style", Georgia, "Times New Roman", serif',
    // Each line stays whole, as in the App Store artwork.
    fontSize: "clamp(38px, 5.2vw, 68px)",
    fontWeight: 700,
    letterSpacing: "-0.01em",
    lineHeight: 1.06,
    marginBottom: 20,
  },
  headlineAccent: {
    color: "#9E5C40",
  },
  headlineLine: {
    display: "block",
    whiteSpace: "nowrap",
  },
  hero: {
    // Colors from the App Store header artwork.
    backgroundColor: "#F7F0E3",
    overflow: "hidden",
    paddingBottom: isMobile ? 56 : 80,
    paddingTop: isMobile ? 56 : 80,
    position: "relative",
  },
  heroArc: {
    backgroundColor: "#E8D9BD",
    borderRadius: "50%",
    height: "150%",
    left: "48%",
    position: "absolute",
    top: "-25%",
    width: "67%",
  },
  heroInner: {
    position: "relative",
  },
  heroPhones: {
    display: "block",
    margin: "0 auto",
    maxHeight: 560,
  },
  heroSubline: {
    color: "#6B5C4F",
    fontSize: isMobile ? 18 : 21,
    lineHeight: 1.5,
    marginBottom: 28,
    maxWidth: 470,
  },
  promoContainer: {
    backgroundColor: "#000",
    paddingTop: 80,
  },
  featureCard: {
    backgroundColor: "#1C1816",
    borderRadius: 8,
    height: "100%",
    padding: 24,
    textAlign: "left",
  },
  featureDescription: {
    color: "#ACACAC",
    fontSize: 16,
    marginBottom: 0,
  },
  featureTitle: {
    color: "#CF3620",
    fontWeight: 700,
    marginBottom: 8,
  },
  featuresContainer: {
    backgroundColor: "#000",
    paddingTop: 80,
    paddingBottom: 80,
  },
  howItWorksContainer: {
    backgroundColor: "#000",
    paddingTop: 40,
    paddingBottom: 56,
  },
  howToCol: {
    marginBottom: 24,
    textAlign: "center",
  },
  howToThumb: {
    display: "block",
    height: "100%",
    objectFit: "cover",
    width: "100%",
  },
  howToThumbFrame: {
    aspectRatio: "9 / 16",
    backgroundColor: "#1C1816",
    borderRadius: 12,
    margin: "0 auto",
    maxWidth: 200,
    overflow: "hidden",
  },
  howToTitle: {
    color: "#FFF",
    fontSize: isMobile ? 13 : 15,
    fontWeight: 600,
    marginTop: 10,
  },
  infoContainer: {
    backgroundColor: "#000",
    paddingBottom: 100,
  },
  infoText: {
    color: "#ACACAC",
    fontSize: 16,
  },
  screenshot: {
    borderRadius: 16,
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.6)",
  },
  screenshotCol: {
    marginBottom: 24,
  },
  screenshotsContainer: {
    backgroundColor: "#000",
    paddingTop: 80,
    paddingBottom: 56,
  },
  sectionTitle: {
    color: "#FFF",
    fontWeight: 700,
    textAlign: "center",
  },
};
